import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import { SiteConfigProvider } from './hooks/useSiteConfig';
import { StripeProvider } from './hooks/useStripe';
import { AdminRoute, PublicRoute } from './components/auth/ProtectedRoute';
import { usePageTracking } from './hooks/useFirebaseAnalytics';
import Layout from './components/guest/layout/Layout';
import DynamicPageTitle from './components/common/DynamicPageTitle';
import Home from './pages/Home';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import AdminDashboard from './pages/admin/Dashboard';
import Customers from './pages/admin/Customers';
import AdminQuotes from './pages/admin/Quotes';
import AdminPayments from './pages/admin/Payments';
import Analytics from './pages/admin/Analytics';
import Content from './pages/admin/Content';
import AdminSettings from './pages/admin/Settings';
import AdminServices from './pages/admin/Services';
import AdminProperties from './pages/admin/Properties';
import './index.css';

// Inner component so usePageTracking can access useLocation (requires being inside <Router>)
function AppRoutesWithTracking() {
  usePageTracking();
  return (
    <Routes>
      {/* Public routes - redirect authenticated users to dashboard */}
      <Route path="/" element={
        <PublicRoute>
          <DynamicPageTitle includeTagline={true}>
            <Layout>
              <Home />
            </Layout>
          </DynamicPageTitle>
        </PublicRoute>
      } />
      
      <Route path="/login" element={
        <PublicRoute>
          <DynamicPageTitle 
            pageTitle="Login"
            customDescription="Sign in to your account to manage your landscaping services and view project updates."
          >
            <Layout>
              <Login />
            </Layout>
          </DynamicPageTitle>
        </PublicRoute>
      } />
      
      <Route path="/forgot-password" element={
        <PublicRoute>
          <DynamicPageTitle pageTitle="Reset Password">
            <Layout>
              <ForgotPassword />
            </Layout>
          </DynamicPageTitle>
        </PublicRoute>
      } />
      
      {/* Admin Dashboard routes - protected and admin-only */}
      <Route path="/admin/dashboard" element={
        <AdminRoute>
          <DynamicPageTitle pageTitle="Admin Dashboard">
            <AdminDashboard />
          </DynamicPageTitle>
        </AdminRoute>
      } />
      
      <Route path="/admin/customers" element={
        <AdminRoute>
          <DynamicPageTitle pageTitle="Manage Customers">
            <Customers />
          </DynamicPageTitle>
        </AdminRoute>
      } />

      <Route path="/admin/properties" element={
        <AdminRoute>
          <DynamicPageTitle pageTitle="Manage Properties">
            <AdminProperties />
          </DynamicPageTitle>
        </AdminRoute>
      } />

      <Route path="/admin/services" element={
        <AdminRoute>
          <DynamicPageTitle pageTitle="Manage Services">
            <AdminServices />
          </DynamicPageTitle>
        </AdminRoute>
      } />
      
      <Route path="/admin/quotes" element={
        <AdminRoute>
          <DynamicPageTitle pageTitle="Manage Quotes">
            <AdminQuotes />
          </DynamicPageTitle>
        </AdminRoute>
      } />
      
      <Route path="/admin/payments" element={
        <AdminRoute>
          <DynamicPageTitle pageTitle="Manage Payments">
            <AdminPayments />
          </DynamicPageTitle>
        </AdminRoute>
      } />
      
      <Route path="/admin/analytics" element={
        <AdminRoute>
          <DynamicPageTitle pageTitle="Analytics">
            <Analytics />
          </DynamicPageTitle>
        </AdminRoute>
      } />
      
      <Route path="/admin/gallery" element={
        <AdminRoute>
          <DynamicPageTitle pageTitle="Manage Gallery">
            <Content />
          </DynamicPageTitle>
        </AdminRoute>
      } />
      
      <Route path="/admin/settings" element={
        <AdminRoute>
          <DynamicPageTitle pageTitle="Admin Settings">
            <AdminSettings />
          </DynamicPageTitle>
        </AdminRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <SiteConfigProvider>
        <StripeProvider>
          <Router>
            <AppRoutesWithTracking />
          </Router>
        </StripeProvider>
      </SiteConfigProvider>
    </AuthProvider>
  );
}

export default App;